import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import '../styles/SidebarMenu.scss';

/**
 * Minimal custom sidebar accordion menu.
 * Replaces PrimeReact PanelMenu to avoid v10 chevron-left alignment quirks.
 *
 * Item shape: { label, icon, path?, command?, items?, visible? }
 * - path: used for active-state detection against location.pathname
 */
const SidebarMenu = ({ items = [] }) => {
  const location = useLocation();
  const [expanded, setExpanded] = useState({});

  const isPathActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isGroupActive = (item) =>
    (item.items || []).some(child => isPathActive(child.path));

  // Auto-expand groups whose child matches the current route
  useEffect(() => {
    const autoExpand = {};
    items.forEach(item => {
      if (item.items?.length > 0 && isGroupActive(item)) {
        autoExpand[item.label] = true;
      }
    });
    setExpanded(prev => ({ ...prev, ...autoExpand }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggle = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const visible = (item) => item.visible !== false;

  return (
    <nav className="smenu">
      {items.filter(visible).map((item, idx) => {
        const children = (item.items || []).filter(visible);
        const hasChildren = children.length > 0;
        const isOpen = !!expanded[item.label];
        const groupActive = hasChildren && isGroupActive(item);
        const open = isOpen || groupActive;

        if (hasChildren) {
          return (
            <div key={idx} className={`smenu-group ${open ? 'smenu-group--open' : ''}`}>
              <button
                className="smenu-header"
                onClick={() => (item.command ? item.command() : toggle(item.label))}
              >
                <i className={item.icon} />
                <span className="smenu-label">{item.label}</span>
                <i
                  className={`smenu-chevron pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                  onClick={(e) => { e.stopPropagation(); toggle(item.label); }}
                />
              </button>
              {open && (
                <div className="smenu-children">
                  {children.map((child, ci) => (
                    <button
                      key={ci}
                      className={`smenu-item${isPathActive(child.path) ? ' smenu-item--active' : ''}`}
                      onClick={child.command}
                    >
                      <i className={child.icon} />
                      <span className="smenu-label">{child.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        }

        // Direct-link item (no children)
        return (
          <button
            key={idx}
            className={`smenu-header${isPathActive(item.path) ? ' smenu-header--active' : ''}`}
            onClick={item.command}
          >
            <i className={item.icon} />
            <span className="smenu-label">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default SidebarMenu;
